<template>
  <div
    class="hc-legend"
    :class="{ 'hc-legend--inactive': disable }"
    role="group"
    aria-label="Chart legend"
  >
    <button
      v-for="item in items"
      :key="item.key"
      type="button"
      class="hc-legend-item"
      :class="{ 'hc-legend-item--inactive': !item.visible }"
      @click="onClick(item)"
      @focus="$emit('item-focus', item)"
      @mouseenter="$emit('item-focus', item)"
      @blur="$emit('item-blur', item)"
      @mouseleave="$emit('item-blur', item)"
    >
      <span class="hc-legend-symbol" :class="symbolClass(item)" :style="{ backgroundColor: item.color }"></span>
      <span class="hc-legend-text">{{ item.name }}</span>
    </button>
  </div>
</template>

<script setup lang="ts">
import { LegendItem, LegendStyle } from "@/guiide/ts/response/charts/types";

const props = withDefaults(defineProps<{ 
  items: LegendItem[],
  disable?: boolean
}>(), {
  disable: false
});

const emit = defineEmits<{
  (e: "item-toggle", item: LegendItem): void
  (e: "item-focus", item: LegendItem): void
  (e: "item-blur", item: LegendItem): void
}>();

function onClick(item: LegendItem) {
  emit('item-toggle', item);
}

function symbolClass(item: LegendItem) {
  return item.style === LegendStyle.Line
    ? "hc-legend-symbol--line"
    : "hc-legend-symbol--circle";
}
</script>

<style scoped>
.hc-legend {
  display: flex;
  flex-wrap: wrap;
  gap: .35rem .9rem;
  justify-content: center;
  align-items: center;
  font-family:
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    Roboto,
    Helvetica,
    Arial,
    "Apple Color Emoji",
    "Segoe UI Emoji",
    "Segoe UI Symbol",
    sans-serif;
}

.hc-legend--inactive {
  opacity: 0.35;
  filter: grayscale(0.25);
  pointer-events: none;
  user-select: none;
}

.hc-legend-item {
  display: inline-flex;
  align-items: center;
  gap: .3rem;
  border: 0;
  background: transparent;
  padding: .25rem .3rem;
  cursor: pointer;
  border-radius: .4rem;
  white-space: nowrap;
}

.hc-legend-item--inactive {
  opacity: 0.6;
  text-decoration: line-through;
}

.hc-legend-item:hover {
  background: rgba(0,0,0,.05);
}

.hc-legend-symbol {
  width: 12px;
  height: 12px;
  border-radius: 2px;
  flex: 0 0 auto;
}

.hc-legend-symbol--circle {
  width: 12px;
  height: 12px;
  border-radius: 50%;
}

.hc-legend-symbol--line {
  width: 15px;
  height: 4px;
  border-radius: 2px;
  margin-top: 1px;
}

.hc-legend-text {
  font-size: 12px; 
  font-weight: 300;
  line-height: 1.1;
  white-space: nowrap;
}
</style>
